import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, CircularProgress, Link, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Chip, IconButton, Tooltip, InputAdornment, TextField, Card, Collapse, Snackbar, Alert, Tabs, Tab } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BusinessIcon from '@mui/icons-material/Business';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationCityIcon from '@mui/icons-material/LocationCity';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import EmailIcon from '@mui/icons-material/Email';
import SourceIcon from '@mui/icons-material/Source';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import LeadFilter from './LeadFilter';
import ApiService from '../../../../services/api';
import PaymentsService from '../../../../services/PaymentsService';
// removed unused imports
// socket client removed: link tracking discontinued
// Trial link generator moved/unused here
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';

import logger from '../../../../utils/logger';
import { formatDate, formatDateTime } from '../../../../utils/dateUtils';
import { useAdminRole } from '../../../../contexts/AdminRoleContext';

const Container = styled(Box)(({ theme }) => ({
    padding: '24px',
    backgroundColor: '#f5f7fa',
    minHeight: '100vh',
}));

const Header = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '16px',
}));

const TitleSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
}));

const TitleIcon = styled(Box)(({ theme }) => ({
    width: '48px',
    height: '48px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
}));

const SearchAndFilter = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: '12px',
    alignItems: 'center',
}));

const StyledSearchField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        backgroundColor: '#fff',
        '&:hover fieldset': {
            borderColor: '#3b82f6',
        },
        '&.Mui-focused fieldset': {
            borderColor: '#3b82f6',
        },
    },
}));

const AddButton = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    padding: '10px 20px',
    borderRadius: '10px',
    fontWeight: 600,
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    boxShadow: '0 4px 14px rgba(59, 130, 246, 0.4)',
    '&:hover': {
        background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 100%)',
        boxShadow: '0 6px 20px rgba(59, 130, 246, 0.5)',
    },
}));

const StatsContainer = styled(Box)(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
}));

const StatCard = styled(Card)(({ theme }) => ({
    padding: '20px',
    borderRadius: '16px',
    background: '#fff',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 25px rgba(0,0,0,0.1)',
    },
}));

const StatIconBox = styled(Box)(({ bgcolor }) => ({
    width: '50px',
    height: '50px',
    borderRadius: '12px',
    background: bgcolor || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
}));

const TableContainerStyled = styled(TableContainer)(({ theme }) => ({
    backgroundColor: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
    overflow: 'hidden',
}));

const Th = styled(TableCell)(({ theme }) => ({
    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
    color: '#ffffff',
    fontWeight: 600,
    padding: '16px',
    fontSize: '14px',
    whiteSpace: 'nowrap',
}));

const Td = styled(TableCell)(({ theme }) => ({
    padding: '14px 16px',
    borderBottom: '1px solid #f0f0f0',
    fontSize: '14px',
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    transition: 'background-color 0.2s',
    '&:hover': {
        backgroundColor: '#f8f9ff',
    },
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
    padding: '8px',
    '&:hover': {
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
    },
}));

const StatusChip = styled(Chip)(({ status }) => {
    const colors = {
        'active': { bg: '#e8f5e9', color: '#2e7d32' },
        'pending': { bg: '#fff3e0', color: '#f57c00' },
        'closed': { bg: '#ffebee', color: '#c62828' },
        'meeting scheduled': { bg: '#fffbf0', color: '#b45309' },
        'new': { bg: '#e3f2fd', color: '#1565c0' },
        'default': { bg: '#f5f5f5', color: '#616161' },
    };
    const normalizeStatus = (s) => {
        if (!s && s !== 0) return '';
        if (typeof s === 'string') return s;
        if (typeof s === 'number' || typeof s === 'boolean') return String(s);
        if (typeof s === 'object') return String(s.status || s.name || s.label || '');
        return '';
    };
    const statusLower = normalizeStatus(status).toLowerCase();
    const colorSet = colors[statusLower] || colors.default;
    return {
        backgroundColor: colorSet.bg,
        color: colorSet.color,
        fontWeight: 600,
        fontSize: '12px',
    };
});

const SourceChip = styled(Chip)(({ source }) => {
    const colors = {
        'instagram': { bg: '#fce4ec', color: '#c2185b' },
        'facebook': { bg: '#e3f2fd', color: '#1565c0' },
        'google': { bg: '#fff8e1', color: '#ff8f00' },
        'referral': { bg: '#e8f5e9', color: '#2e7d32' },
        'advertisement': { bg: '#f3e5f5', color: '#7b1fa2' },
        'whatsapp': { bg: '#e8f5e9', color: '#25d366' },
        'website': { bg: '#e0f7fa', color: '#00838f' },
        'website signup': { bg: '#c8e6c9', color: '#1b5e20' },
        'cold call': { bg: '#fff3e0', color: '#e65100' },
        'trial': { bg: '#c8e6c9', color: '#1b5e20' },
        'default': { bg: '#f5f5f5', color: '#616161' },
    };
    const normalizeSource = (s) => {
        if (!s && s !== 0) return '';
        if (typeof s === 'string') return s;
        if (typeof s === 'number' || typeof s === 'boolean') return String(s);
        if (typeof s === 'object') return String(s.source || s.name || s.label || '');
        return '';
    };
    const sourceLower = normalizeSource(source).toLowerCase();
    const colorSet = colors[sourceLower] || colors.default;
    return {
        backgroundColor: colorSet.bg,
        color: colorSet.color,
        fontWeight: 500,
        fontSize: '12px',
    };
});

const Pagination = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '24px',
    padding: '0 8px',
}));

const PaginationButton = styled(Button)(({ theme }) => ({
    textTransform: 'none',
    borderRadius: '8px',
    padding: '8px 16px',
    fontWeight: 500,
}));
const LeadListing = () => {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(1);
    const pageSize = 10;
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const navigate = useNavigate();
    const [filterOpen, setFilterOpen] = useState(false);
    // anchor menu removed (unused)
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [leadToDelete, setLeadToDelete] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [deleteLoading, setDeleteLoading] = useState(false);
    // Lead type filter: 'all', 'signup', 'manual'
    const [leadTypeFilter, setLeadTypeFilter] = useState('manual');
    // removed modal-based generator; using Quick Generate instead

    // Role-based access
    const { getFilterParams, isSalesTeam, getRoleName, isSuperAdmin, isAdmin } = useAdminRole();

    const normalizeForCompare = (v) => {
        if (v === null || v === undefined) return '';
        if (typeof v === 'string') return v.toLowerCase();
        if (typeof v === 'number' || typeof v === 'boolean') return String(v).toLowerCase();
        if (typeof v === 'object') return String(v.status || v.name || v.label || '').toLowerCase();
        return '';
    };

    useEffect(() => {
        if (!isSuperAdmin && !isAdmin) {
            setLeadTypeFilter('manual');
        }
    }, [isSuperAdmin, isAdmin]);

    useEffect(() => {
        fetchLeads(page, searchQuery, leadTypeFilter);
    }, [page, searchQuery, leadTypeFilter]);

    const fetchLeads = useCallback(async (targetPage = 1, q = '', typeFilter = 'all') => {
        setLoading(true);
        setError(null);
        try {
            const isSearching = !!(q && q.trim());
            const base = isSearching
                ? `/admin/leads/search-leads?globleSearch=${encodeURIComponent(q.trim())}&type=lead`
                : `/admin/leads/get-leads?type=lead`;
            // Add role-based filtering params
            const roleParams = getFilterParams();
            const url = `${base}&page=${targetPage}&size=${pageSize}${roleParams}`;
            console.log('🔍 Fetching leads from:', url);
            const response = await ApiService.get(url);
            console.log('📦 Raw API Response:', response);
            const root = response?.data ?? response;
            console.log('📦 Root data:', root);
            const collections = [
                root?.data?.leads?.userDetails?.employeeDetails?.role,
                root?.leads,
                root?.data?.items,
                root?.data?.data,
                root?.data?.list,
                root?.data?.rows,
                root?.data?.result,
                root?.items,
                root?.list,
                root?.rows,
                root?.result,
                root?.content, // Spring Page
                root?.records,
                root?.data, // Sometimes just the array
            ];
            const found = collections.find(arr => Array.isArray(arr));
            console.log('📋 Found leads array:', found);
            let items = Array.isArray(found) ? found : [];
            console.log('📋 Items count:', items.length);
            // Client-side fallback filter for business_name, status, lead_source when using globleSearch
            if (isSearching) {
                const ql = q.trim().toLowerCase();
                items = items.filter((lead) => {
                    const business = (lead.business_name || lead.name || '').toString().toLowerCase();
                    const status = lead.meeting_scheduled
                        ? 'meeting scheduled'
                        : (lead.status || '').toString().toLowerCase();
                    const source = (lead.lead_source || lead.source || '').toString().toLowerCase();
                    return business.includes(ql) || status.includes(ql) || source.includes(ql);
                });
            }
            // Filter by lead source (signup vs manual)
            if (typeFilter && typeFilter !== 'all') {
                items = items.filter((lead) => {
                    const source = (lead.lead_source || lead.source || '').toString().toLowerCase();
                    // Signup sources: Website Signup, Trial, or auto-created leads
                    const signupSources = ['website signup', 'trial', 'signup', 'website'];
                    const isSignup = signupSources.some(s => source.includes(s)) ||
                        lead.type === 'trial' ||
                        (lead.comment && lead.comment.toLowerCase().includes('auto-created'));

                    if (typeFilter === 'signup') {
                        return isSignup;
                    } else if (typeFilter === 'manual') {
                        return !isSignup;
                    }
                    return true;
                });
            }
            setLeads(items);
            const meta = root?.data ?? root;
            const ti =
                meta?.totalItems ??
                meta?.totalCount ??
                meta?.totalElements ??
                (Array.isArray(items) ? items.length : 0);
            const tp = meta?.totalPages ?? (ti ? Math.max(1, Math.ceil(ti / pageSize)) : 1);
            const cp = meta?.currentPage ?? meta?.page ?? targetPage;
            setTotalItems(Number.isFinite(ti) ? ti : 0);
            setTotalPages(Number.isFinite(tp) ? tp : 1);
            if (Number.isFinite(cp)) setPage(cp);
        } catch (err) {
            logger.error('Failed to fetch leads from API:', err);

            // Check if it's a database table error
            if (err.message && err.message.includes("doesn't exist")) {
                setError('Database configuration issue. Please contact system administrator.');
                logger.warn('Database table missing - this needs to be fixed on the backend');
            } else {
                setError('Failed to fetch leads. Please try again later.');
            }

            setLeads([]);
            setTotalItems(0);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
    }, []);

    const handleDeleteClick = (lead) => {
        setLeadToDelete(lead);
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!leadToDelete?.id) {
            logger.warn('No lead selected to delete');
            setDeleteDialogOpen(false);
            return;
        }
        setDeleteLoading(true);
        try {
            await ApiService.delete(`/admin/leads/delete-leads/${leadToDelete.id}`);
            // Remove the deleted lead from local state immediately
            setLeads(prev => prev.filter(l => l.id !== leadToDelete.id));
            setTotalItems(prev => Math.max(0, prev - 1));
            setDeleteDialogOpen(false);
            setLeadToDelete(null);
            // Refresh the list
            fetchLeads(page, searchQuery, leadTypeFilter);
        } catch (err) {
            logger.error('Failed to delete lead:', err);
            alert('Failed to delete lead. Please try again.');
        } finally {
            setDeleteLoading(false);
        }
    };

    const handleDeleteCancel = () => {
        if (!deleteLoading) {
            setDeleteDialogOpen(false);
            setLeadToDelete(null);
        }
    };

    // handleAddLead removed (unused)

    const [quickVisitUrl, setQuickVisitUrl] = useState(null);
    const [quickLoading, setQuickLoading] = useState(false);
    const [quickCopied, setQuickCopied] = useState(false);
    const [quickVisible, setQuickVisible] = useState(false);

    // Link tracking removed: coupon-only tracking enabled

    // getTrackingCountForLead removed (unused)

    const handleQuickGenerate = async () => {
        setQuickLoading(true);
        // Show fixed coupon immediately for UX
        const FIXED_COUPON = 'E2Evisibeen';
        setQuickVisitUrl(FIXED_COUPON);
        setQuickVisible(true);
        try {
            const body = { plan_code: 'basic', days: 3650, single_use: false };
            const resp = await ApiService.post('/customer/auth/trial-links', body);
            const data = resp?.data || resp;
            const coupon = data?.coupon || data?.token || resp?.coupon || (resp?.data && resp.data.coupon) || null;
            if (coupon) setQuickVisitUrl(coupon);
        } catch (err) {
            logger.log('Failed to generate trial link:', err?.message || err);
        } finally {
            setQuickLoading(false);
        }
    };

    const handleQuickCopy = async () => {
        if (!quickVisitUrl) return;
        try {
            await navigator.clipboard.writeText(quickVisitUrl);
            setQuickCopied(true);
            setTimeout(() => setQuickCopied(false), 2500);
            // hide dropdown after copy
            setQuickVisible(false);
        } catch (e) { }
    };

    // Coupon tracking dialog state
    const [couponTrackOpen, setCouponTrackOpen] = useState(false);
    const [couponInput, setCouponInput] = useState('');
    const [couponUsages, setCouponUsages] = useState([]);
    const [couponResyncTried, setCouponResyncTried] = useState(false);
    const [couponLoading, setCouponLoading] = useState(false);
    const [couponFetchDebug, setCouponFetchDebug] = useState(null);

    const FIXED_COUPON = 'E2Evisibeen';

    const fetchCouponUsages = async (token) => {
        if (!token) {
            setCouponFetchDebug('no-token');
            return;
        }
        setCouponLoading(true);
        try {
            setCouponFetchDebug('direct-fetch-start');
            const resp = await ApiService.get(`/trial/link/${encodeURIComponent(token)}/usages`);
            const payload = resp?.data || resp;
            let usages = payload?.usages || payload || [];
            setCouponFetchDebug(`direct-fetch:${Array.isArray(usages) ? usages.length : 0}`);

            // Fallback: if no usages found for the direct token lookup, try a broader admin search
            if ((!Array.isArray(usages) || usages.length === 0) && token) {
                try {
                    setCouponFetchDebug('fallback-search-start');
                    const sresp = await ApiService.get(`/trial/search?q=${encodeURIComponent(token)}`);
                    const sp = sresp?.data || sresp;
                    // normalize results from backend: use sp.results if present
                    const searchResults = sp?.results || sp || [];
                    setCouponFetchDebug(`fallback-search:${Array.isArray(searchResults) ? searchResults.length : 0}`);
                    if (Array.isArray(searchResults) && searchResults.length > 0) {
                        usages = searchResults;
                    }
                } catch (se) {
                    logger.warn('Fallback trial search failed', se);
                    setCouponFetchDebug('fallback-search-failed');
                }
            }

            // Final fallback: if still no usages, try to read client-side history (localStorage or cookies)
            if ((!Array.isArray(usages) || usages.length === 0)) {
                try {
                    const histUsages = [];
                    // localStorage keys used elsewhere in the app/payment flow
                    const lsToken = window.localStorage.getItem('came_from_trial') || window.localStorage.getItem('came_from_trial_token');
                    const lsSig = window.localStorage.getItem('came_from_trial_sig');
                    const lsLocation = window.localStorage.getItem('came_from_trial_location');
                    const lsTs = window.localStorage.getItem('came_from_trial_ts');
                    // also check sessionStorage (some flows use it)
                    const ssToken = window.sessionStorage ? (window.sessionStorage.getItem('came_from_trial') || window.sessionStorage.getItem('came_from_trial_token')) : null;
                    const ssSig = window.sessionStorage ? window.sessionStorage.getItem('came_from_trial_sig') : null;
                    const ssLocation = window.sessionStorage ? window.sessionStorage.getItem('came_from_trial_location') : null;
                    const ssTs = window.sessionStorage ? window.sessionStorage.getItem('came_from_trial_ts') : null;
                    const choose = (a, b) => a || b || null;
                    if (lsToken || ssToken || lsSig || ssSig || lsLocation || ssLocation) {
                        const chosenToken = choose(lsToken, ssToken) || token;
                        const chosenSig = choose(lsSig, ssSig);
                        const chosenLoc = choose(lsLocation, ssLocation);
                        const chosenTs = choose(lsTs, ssTs);
                        histUsages.push({
                            token: chosenToken,
                            used_at: chosenTs ? (isNaN(Number(chosenTs)) ? chosenTs : new Date(Number(chosenTs)).toISOString()) : null,
                            used_email: null,
                            used_phone: null,
                            gmb_profile_location_id: chosenLoc || null,
                            paid_at: chosenTs ? (isNaN(Number(chosenTs)) ? chosenTs : new Date(Number(chosenTs)).toISOString()) : null,
                            paid_amount: '₹1',
                            notes: JSON.stringify({ source: 'client_history', sig: chosenSig || null })
                        });
                    }

                    // Also check cookies for legacy came_from_trial entries
                    if (document && document.cookie) {
                        const parts = document.cookie.split(';').map(p => p.trim());
                        const t = parts.find(p => p.startsWith('came_from_trial='));
                        const s = parts.find(p => p.startsWith('came_from_trial_sig='));
                        const l = parts.find(p => p.startsWith('came_from_trial_location='));
                        const ckToken = t ? decodeURIComponent(t.split('=')[1] || '') : null;
                        const ckSig = s ? decodeURIComponent(s.split('=')[1] || '') : null;
                        const ckLoc = l ? decodeURIComponent(l.split('=')[1] || '') : null;
                        if (ckToken || ckSig || ckLoc) {
                            histUsages.push({
                                token: ckToken || token,
                                used_at: null,
                                used_email: null,
                                used_phone: null,
                                gmb_profile_location_id: ckLoc || null,
                                notes: JSON.stringify({ source: 'cookie', sig: ckSig || null })
                            });
                        }
                    }

                    if (histUsages.length > 0) {
                        usages = histUsages;
                        setCouponFetchDebug(`client-history:${histUsages.length}`);
                    }
                } catch (hx) {
                    logger.warn('Reading trial history fallback failed', hx);
                    setCouponFetchDebug('client-history-failed');
                }
            }

            // If still empty, try to fetch payments history matching the token
            if ((!Array.isArray(usages) || usages.length === 0)) {
                try {
                    const presp = await PaymentsService.fetchPayments({ q: token, limit: 200 });
                    const pp = presp?.data || presp;
                    const payments = pp?.data || pp?.payments || pp || [];
                    if (Array.isArray(payments) && payments.length > 0) {
                        const paymentUsages = payments.map(p => ({
                            used_email: p.email || p.customer_email || p.user_email || (p.user && p.user.email) || null,
                            used_phone: p.phone || p.customer_phone || p.user_phone || (p.user && p.user.phone) || null,
                            gmb_profile_location_id: p.gmb_location_id || p.location_id || (p.metadata && p.metadata.gmb_profile_location_id) || null,
                            plan_id: p.plan || p.plan_id || null,
                            status: p.status || p.payment_status || p.state || p.razorpay_status || null,
                            paid_at: p.paid_at || p.created_at || p.createdAt || p.timestamp || null,
                            paid_amount: p.amount || p.paid_amount || p.razorpay_amount || null,
                            notes: JSON.stringify({ source: 'payments_history', matched_token: true })
                        }));
                        usages = paymentUsages;
                        logger.log('[LeadListing] Using payments history matched by token, count:', paymentUsages.length);
                        setCouponFetchDebug(`payments-token:${paymentUsages.length}`);
                    }
                } catch (pe) {
                    logger.warn('Fallback payments fetch failed', pe);
                    setCouponFetchDebug('payments-token-failed');
                }
            }

            // NOTE: removed recent-trials fallback to avoid showing unrelated data

            // If no usages found after all fallbacks, try an admin resync once (backfill coupon_usages), then synthetic
            if ((!Array.isArray(usages) || usages.length === 0) && !couponResyncTried) {
                try {
                    setCouponFetchDebug('resync-start');
                    // admin endpoint will backfill coupon_usages from legacy trials/payments
                    await ApiService.post(`/trial/resync?token=${encodeURIComponent(token)}&force=true`);
                    setCouponResyncTried(true);
                    // re-fetch usages after resync
                    const rresp = await ApiService.get(`/trial/link/${encodeURIComponent(token)}/usages`);
                    const rpayload = rresp?.data || rresp;
                    usages = rpayload?.usages || rpayload || [];
                    setCouponFetchDebug(`resync-fetch:${Array.isArray(usages) ? usages.length : 0}`);
                } catch (rs) {
                    logger.warn('Resync attempt failed', rs);
                    setCouponFetchDebug('resync-failed');
                }
            }

            // If no usages found after all fallbacks, keep usages empty (do not show synthetic rows)
            if ((!Array.isArray(usages) || usages.length === 0)) {
                usages = [];
            }

            setCouponUsages(usages || []);
            // ensure debug state set if usages exist but no debug message assigned
            if ((!couponFetchDebug || couponFetchDebug === 'no-token') && Array.isArray(usages) && usages.length > 0) {
                setCouponFetchDebug(`usages:${usages.length}`);
            }
        } catch (e) {
            logger.warn('Failed to fetch coupon usages', e);
            setCouponUsages([]);
            try {
                const status = e && e.status ? e.status : null;
                const body = e && e.body ? JSON.stringify(e.body) : (e && e.message ? e.message : String(e));
                setCouponFetchDebug(`error${status ? ':' + status : ''} ${body}`);
                console.error('[LeadListing] fetchCouponUsages error details:', e);
            } catch (pe) {
                setCouponFetchDebug('fetch-failed-exception');
            }
        } finally { setCouponLoading(false); }
    };

    // Auto-fetch usages when dialog opens for the single fixed coupon
    useEffect(() => {
        if (!couponTrackOpen) return;
        const tokenToUse = quickVisitUrl || FIXED_COUPON;
        setCouponInput(tokenToUse);
        fetchCouponUsages(tokenToUse);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [couponTrackOpen]);

    // Link tracking removed — tracking UI and event streams eliminated to keep coupon-only tracking

    // fetchEventsPage removed — link event pages are not used anymore

    return (
        <Container>
            <Header>
                <TitleSection>
                    <TitleIcon>
                        <PeopleIcon fontSize="large" />
                    </TitleIcon>
                    <Box>
                        <Typography variant="h4" fontWeight={700} color="#2c3e50">Lead Management</Typography>
                        <Typography variant="body2" color="textSecondary">Track and manage your potential clients</Typography>

                        <Snackbar open={quickCopied} autoHideDuration={2000} onClose={() => setQuickCopied(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                            <Alert onClose={() => setQuickCopied(false)} severity="success" sx={{ bgcolor: '#16a34a', color: '#fff' }}>Copied successfully</Alert>
                        </Snackbar>
                    </Box>
                </TitleSection>
                <SearchAndFilter>
                    <StyledSearchField
                        placeholder="Search leads..."
                        value={searchQuery}
                        onChange={(e) => {
                            setPage(1);
                            setSearchQuery(e.target.value);
                        }}
                        size="small"
                        sx={{ width: 280 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon sx={{ color: '#3b82f6' }} />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <AddButton variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/admin-dashboard/add-lead')}>
                        Add Lead
                    </AddButton>
                    {/* Generate Trial Coupon (replaces link) */}
                    {(isSuperAdmin || isAdmin) && (
                        <Box sx={{ position: 'relative' }}>
                            <AddButton onClick={handleQuickGenerate} sx={{ color: '#fff' }}>
                                {quickLoading ? 'Generating…' : 'Generate Coupon'}
                            </AddButton>

                            <Collapse in={quickVisible} timeout={200} unmountOnExit>
                                <Box sx={{ position: 'absolute', top: 'calc(100% + 8px)', left: 0, zIndex: 1200 }}>
                                    <Box sx={{ p: 1.25, minWidth: 220, borderRadius: '14px', background: '#fff', border: '1px solid #eef2ff', boxShadow: '0 8px 20px rgba(2,6,23,0.06)', display: 'flex', alignItems: 'center', gap: 1.25 }}>
                                        <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', mr: 1 }}>
                                            <Typography sx={{ color: '#0b91d6', fontWeight: 900, fontSize: 16 }}>{quickVisitUrl}</Typography>
                                            <Typography sx={{ fontSize: 11, color: '#64748b', mt: 0.25 }}>Coupon</Typography>
                                        </Box>

                                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                            <Button onClick={handleQuickCopy} variant="contained" size="small" startIcon={quickCopied ? <CheckIcon /> : <ContentCopyIcon />} sx={{ background: 'linear-gradient(90deg,#7c3aed,#06b6d4)', color: '#fff', textTransform: 'none', minWidth: 72, px: 1.25 }}>
                                                {quickCopied ? 'Copied' : 'Copy'}
                                            </Button>
                                        </Box>
                                    </Box>

                                    <Box sx={{ position: 'absolute', top: -8, left: 20, width: 16, height: 16, background: '#fff', transform: 'rotate(45deg)', border: '1px solid #eef2ff', borderRadius: 2, boxShadow: '0 4px 10px rgba(2,6,23,0.04)' }} />
                                </Box>
                            </Collapse>
                        </Box>
                    )}
                </SearchAndFilter>
            </Header>




            {/* Lead Type Filter Tabs - Visible only to Admins */}
            {(isSuperAdmin || isAdmin) && (
                <Box sx={{ mb: 3 }}>
                    <Tabs
                        value={leadTypeFilter}
                        onChange={(e, newValue) => { setLeadTypeFilter(newValue); setPage(1); }}
                        sx={{
                            backgroundColor: '#fff',
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                            padding: '4px',
                            minHeight: 'auto',
                            '& .MuiTab-root': {
                                minHeight: '44px',
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 600,
                                fontSize: '14px',
                                color: '#64748b',
                                transition: 'all 0.2s ease',
                                '&.Mui-selected': {
                                    background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                                    color: '#fff',
                                },
                                '&:hover': {
                                    backgroundColor: 'rgba(59, 130, 246, 0.1)',
                                },
                            },
                            '& .MuiTabs-indicator': {
                                display: 'none',
                            },
                        }}
                    >
                        <Tab value="manual" label="Manual Leads" icon={<BusinessIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
                        <Tab value="signup" label="Signup Leads" icon={<PersonAddIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
                        <Tab value="all" label="All Leads" icon={<PeopleIcon sx={{ fontSize: 18 }} />} iconPosition="start" />
                    </Tabs>
                    <Typography variant="body2" color="textSecondary" sx={{ mt: 1, ml: 1 }}>
                        {leadTypeFilter === 'all' && 'Showing all leads'}
                        {leadTypeFilter === 'signup' && 'Showing leads from user signups and trial registrations'}
                        {leadTypeFilter === 'manual' && 'Showing manually added leads'}
                    </Typography>
                </Box>
            )}

            <TableContainerStyled>
                <Table>
                    <TableHead>
                        {leadTypeFilter === 'signup' ? (
                            // Signup leads: Limited columns - Name, Email, Phone, Source, Created, Status, Actions
                            <TableRow>
                                <Th><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><PersonIcon fontSize="small" /> Name</Box></Th>
                                <Th><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><EmailIcon fontSize="small" /> Email</Box></Th>
                                <Th><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><PhoneIcon fontSize="small" /> Phone</Box></Th>
                                <Th><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><SourceIcon fontSize="small" /> Source</Box></Th>
                                <Th><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CalendarTodayIcon fontSize="small" /> Created</Box></Th>
                                <Th>Status</Th>
                                <Th align="center">Actions</Th>
                            </TableRow>
                        ) : (
                            // Manual/All leads: Full columns (removed Tele and BDM columns)
                            <TableRow>
                                <Th><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><PersonIcon fontSize="small" /> Name</Box></Th>
                                <Th><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><BusinessIcon fontSize="small" /> Business</Box></Th>
                                <Th><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><SourceIcon fontSize="small" /> Source</Box></Th>
                                <Th><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><CalendarTodayIcon fontSize="small" /> Created</Box></Th>
                                <Th>Status</Th>
                                <Th><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><PhoneIcon fontSize="small" /> Phone</Box></Th>
                                <Th><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><LocationCityIcon fontSize="small" /> City</Box></Th>
                                <Th align="center">Actions</Th>
                            </TableRow>
                        )}
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <Td colSpan={leadTypeFilter === 'signup' ? 7 : 8} align="center" sx={{ py: 5 }}>
                                    <CircularProgress sx={{ color: '#667eea' }} />
                                    <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>Loading leads...</Typography>
                                </Td>
                            </TableRow>
                        ) : error ? (
                            <TableRow>
                                <Td colSpan={leadTypeFilter === 'signup' ? 7 : 8} align="center" sx={{ py: 5 }}>
                                    <Typography color="error">{error}</Typography>
                                </Td>
                            </TableRow>
                        ) : leads.length === 0 ? (
                            <TableRow>
                                <Td colSpan={leadTypeFilter === 'signup' ? 7 : 8} align="center" sx={{ py: 5 }}>
                                    <PeopleIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
                                    <Typography variant="body1" color="textSecondary">No leads found</Typography>
                                    <Typography variant="body2" color="textSecondary">Try adjusting your search or add a new lead</Typography>
                                </Td>
                            </TableRow>
                        ) : (
                            leads.map((lead) => {
                                const id = lead.id || lead._id || lead.lead_id;
                                const chipStatusLabel = lead.meeting_scheduled ? 'Meeting Scheduled' : (lead.status || 'N/A');
                                const chipStatusValue = lead.meeting_scheduled ? 'meeting scheduled' : (lead.status || 'n/a');

                                // Signup leads: Limited columns - Name, Email, Phone, Source, Created, Status, Actions
                                if (leadTypeFilter === 'signup') {
                                    return (
                                        <StyledTableRow key={id}>
                                            <Td>
                                                <Link component={RouterLink} to={`/admin-dashboard/lead/${id}`} sx={{ textDecoration: 'none' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <PersonIcon sx={{ color: '#667eea' }} />
                                                        <Typography variant="subtitle2" fontWeight={600} color="#2c3e50">
                                                            {lead.contact_person || lead.first_name || lead.name || 'N/A'}
                                                        </Typography>
                                                    </Box>
                                                </Link>
                                            </Td>
                                            <Td>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <EmailIcon sx={{ fontSize: 16, color: '#667eea' }} />
                                                    <Typography variant="body2">{lead.email || lead.contact_email || 'N/A'}</Typography>
                                                </Box>
                                            </Td>
                                            <Td>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <PhoneIcon sx={{ fontSize: 16, color: '#667eea' }} />
                                                    <Typography variant="body2">{lead.phone_number || lead.phone || lead.mobile || 'N/A'}</Typography>
                                                </Box>
                                            </Td>
                                            <Td>
                                                <SourceChip
                                                    source={lead.lead_source || lead.source}
                                                    label={lead.lead_source || lead.source || 'N/A'}
                                                    size="small"
                                                />
                                            </Td>
                                            <Td>
                                                <Typography variant="body2" color="textSecondary">
                                                    {lead.created_at || lead.createdAt ? formatDate(lead.created_at || lead.createdAt) : 'N/A'}
                                                </Typography>
                                            </Td>
                                            <Td>
                                                <StatusChip status={chipStatusValue} label={chipStatusLabel} size="small" />
                                            </Td>
                                            <Td align="center">
                                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center' }}>
                                                    <Tooltip title="View Details">
                                                        <ActionButton onClick={() => navigate(`/admin-dashboard/lead/${id}`)} sx={{ color: '#667eea' }}>
                                                            <VisibilityIcon fontSize="small" />
                                                        </ActionButton>
                                                    </Tooltip>
                                                    <Tooltip title="Edit Lead">
                                                        <ActionButton onClick={() => navigate(`/admin-dashboard/edit-lead/${id}`)} sx={{ color: '#f5a623' }}>
                                                            <EditIcon fontSize="small" />
                                                        </ActionButton>
                                                    </Tooltip>
                                                    <Tooltip title="Delete Lead">
                                                        <ActionButton onClick={() => handleDeleteClick(lead)} sx={{ color: '#e74c3c' }}>
                                                            <DeleteIcon fontSize="small" />
                                                        </ActionButton>
                                                    </Tooltip>
                                                </Box>
                                            </Td>
                                        </StyledTableRow>
                                    );
                                }

                                // Manual/All leads: Full columns
                                return (
                                    <StyledTableRow key={id}>
                                        <Td>
                                            <Link component={RouterLink} to={`/admin-dashboard/lead/${id}`} sx={{ textDecoration: 'none' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <PersonIcon sx={{ color: '#667eea' }} />
                                                    <Typography variant="subtitle2" fontWeight={600} color="#2c3e50">
                                                        {lead.contact_person || lead.name || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Link>
                                        </Td>
                                        <Td>
                                            <Link component={RouterLink} to={`/admin-dashboard/lead/${id}`} sx={{ textDecoration: 'none' }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Box sx={{
                                                        width: 36,
                                                        height: 36,
                                                        borderRadius: '8px',
                                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: '#fff',
                                                        fontWeight: 600,
                                                        fontSize: '14px'
                                                    }}>
                                                        {(lead.business_name || lead.name || 'N')[0].toUpperCase()}
                                                    </Box>
                                                    <Typography variant="subtitle2" fontWeight={600} color="#2c3e50">
                                                        {lead.business_name || lead.name || 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </Link>
                                        </Td>
                                        <Td>
                                            <SourceChip
                                                source={lead.lead_source || lead.source}
                                                label={lead.lead_source || lead.source || 'N/A'}
                                                size="small"
                                            />
                                        </Td>
                                        <Td>
                                            <Typography variant="body2" color="textSecondary">
                                                {lead.created_at || lead.createdAt ? formatDate(lead.created_at || lead.createdAt) : 'N/A'}
                                            </Typography>
                                        </Td>
                                        <Td>
                                            <StatusChip status={chipStatusValue} label={chipStatusLabel} size="small" />
                                        </Td>
                                        <Td>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <PhoneIcon sx={{ fontSize: 16, color: '#667eea' }} />
                                                <Typography variant="body2">{lead.phone_number || lead.phone || lead.mobile || 'N/A'}</Typography>
                                            </Box>
                                        </Td>
                                        <Td>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <LocationCityIcon sx={{ fontSize: 16, color: '#667eea' }} />
                                                <Typography variant="body2">{lead.city || 'N/A'}</Typography>
                                            </Box>
                                        </Td>
                                        <Td align="center">
                                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', alignItems: 'center' }}>
                                                <Tooltip title="View Details">
                                                    <ActionButton onClick={() => navigate(`/admin-dashboard/lead/${id}`)} sx={{ color: '#667eea' }}>
                                                        <VisibilityIcon fontSize="small" />
                                                    </ActionButton>
                                                </Tooltip>
                                                <Tooltip title="Edit Lead">
                                                    <ActionButton onClick={() => navigate(`/admin-dashboard/edit-lead/${id}`)} sx={{ color: '#f5a623' }}>
                                                        <EditIcon fontSize="small" />
                                                    </ActionButton>
                                                </Tooltip>
                                                <Tooltip title="Delete Lead">
                                                    <ActionButton onClick={() => handleDeleteClick(lead)} sx={{ color: '#e74c3c' }}>
                                                        <DeleteIcon fontSize="small" />
                                                    </ActionButton>
                                                </Tooltip>
                                            </Box>
                                        </Td>
                                    </StyledTableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainerStyled>
            {/* Trial dialog removed; Quick Generate button handles link creation */}

            <Pagination>
                <Typography variant="body2" color="textSecondary">
                    Showing {leads.length} of {totalItems} leads
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <PaginationButton
                        variant="outlined"
                        disabled={page <= 1}
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        sx={{ borderColor: '#667eea', color: '#667eea' }}
                    >
                        Previous
                    </PaginationButton>
                    <PaginationButton
                        variant="contained"
                        disabled={page >= totalPages}
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
                    >
                        Next
                    </PaginationButton>
                </Box>
            </Pagination>
            <LeadFilter open={filterOpen} onClose={() => setFilterOpen(false)} anchorEl={null} />
            <Dialog
                open={deleteDialogOpen}
                onClose={handleDeleteCancel}
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                        minWidth: '400px',
                    }
                }}
            >
                <DialogTitle sx={{
                    background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                    color: 'white',
                    fontWeight: 700,
                    fontSize: '18px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                }}>
                    <DeleteIcon sx={{ fontSize: 24 }} />
                    Confirm Delete
                </DialogTitle>
                <DialogContent sx={{ pt: 3, pb: 2 }}>
                    <DialogContentText sx={{ fontSize: '15px', color: '#374151' }}>
                        Are you sure you want to delete the lead <strong>"{leadToDelete?.business_name || leadToDelete?.contact_person || 'this lead'}"</strong>?
                        <br /><br />
                        <span style={{ color: '#ef4444', fontWeight: 500 }}>This action cannot be undone.</span>
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
                    <Button
                        onClick={handleDeleteCancel}
                        disabled={deleteLoading}
                        variant="outlined"
                        sx={{
                            borderRadius: '10px',
                            px: 3,
                            py: 1,
                            textTransform: 'none',
                            fontWeight: 600,
                            borderColor: '#d1d5db',
                            color: '#6b7280',
                            '&:hover': {
                                borderColor: '#9ca3af',
                                backgroundColor: '#f3f4f6',
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        disabled={deleteLoading}
                        variant="contained"
                        sx={{
                            borderRadius: '10px',
                            px: 3,
                            py: 1,
                            textTransform: 'none',
                            fontWeight: 600,
                            background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                            boxShadow: '0 4px 15px rgba(239, 68, 68, 0.3)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
                                boxShadow: '0 6px 20px rgba(239, 68, 68, 0.4)',
                            },
                            '&:disabled': {
                                background: '#fca5a5',
                            }
                        }}
                    >
                        {deleteLoading ? (
                            <>
                                <CircularProgress size={18} sx={{ color: 'white', mr: 1 }} />
                                Deleting...
                            </>
                        ) : (
                            'Delete Lead'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default LeadListing;